/**
 * @file PlanTypeCategoriesItemsTab.js
 * @description Categories and Items tab inside the Display Properties modal.
 */

import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Spinner from '../../ui/spinners/Spinner';
import NotFoundBox from '../../ui/no_data_indicators/NotFoundBox';
import Toast from '../../ui/toasts/Toast';
import {
    fetchPlanTypeDetailCategories,
    fetchPlanTypeDetail,
    updatePlanTypeDetailCategories,
    updatePlanTypeDetail,
} from '../../../services/benefitPlanService';
import styles from '../PlanTypeDisplayProperties.module.scss';
import { getText } from '../../../i18n';

const VIEWS = { CATEGORIES: 'categories', ITEMS: 'items' };

const mapCategoryRow = c => ({
    id: c.id,
    clientPlanTypeId: c.clientPlanTypeId,
    systemPlanDetailCategoryId: c.systemPlanDetailCategoryId,
    sortOrder: c.sortOrder,
    isActive: c.isActive,
    isVisible: c.isActive,
    systemName: c.systemPlanDetailCategory?.name,
    categoryName: c.name || c.systemPlanDetailCategory?.name,
    nameOverride: c.name ?? '',
    planTypeDetails: c.planTypeDetails ?? [],
    planYearId: c.planYearId,
});

const mapDetailRow = d => ({
    id: d.id,
    planTypeDetailCategoryId: d.planTypeDetailCategoryId,
    systemPlanDetailId: d.systemPlanDetailId,
    sortOrder: d.sortOrder,
    isActive: d.isActive,
    isVisible: d.isActive,
    systemName: d.systemPlanDetail?.name,
    detailName: d.name || d.systemPlanDetail?.name,
    nameOverride: d.name ?? '',
});

const sortBySortOrder = arr =>
    [...arr].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

const PlanTypeCategoriesItemsTab = ({
    planTypeId,
    planYearId,
    onDirtyChange,
}) => {
    const [view, setView] = useState(VIEWS.CATEGORIES);
    const [activeCategory, setActiveCategory] = useState(null);

    const [categories, setCategories] = useState([]);
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [editNames, setEditNames] = useState(false);

    const [draggedIndex, setDraggedIndex] = useState(null);
    const [hoveredIndex, setHoveredIndex] = useState(null);

    const loadCategories = useCallback(async () => {
        if (!planTypeId) return;
        try {
            setIsLoading(true);
            const fetched = await fetchPlanTypeDetailCategories(
                planTypeId,
                planYearId,
            );
            setCategories(sortBySortOrder((fetched || []).map(mapCategoryRow)));
        } catch (error) {
            console.error('Failed to fetch plan type categories', error);
            Toast.error(getText('msg_4b7be0f9e44e'));
        } finally {
            setIsLoading(false);
        }
    }, [planTypeId, planYearId]);

    const loadItems = useCallback(async category => {
        if (!category?.id) return;
        try {
            setIsLoading(true);
            const fetched = await fetchPlanTypeDetail(category.id);
            setItems(sortBySortOrder((fetched || []).map(mapDetailRow)));
        } catch (error) {
            console.error('Failed to fetch plan type items', error);
            Toast.error(getText('msg_18410f31c4bb'));
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadCategories();
    }, [loadCategories]);

    const enterItems = async category => {
        setActiveCategory(category);
        setEditNames(false);
        setView(VIEWS.ITEMS);
        await loadItems(category);
    };

    const backToCategories = () => {
        setActiveCategory(null);
        setEditNames(false);
        setView(VIEWS.CATEGORIES);
        setItems([]);
    };

    // ─── visibility ───
    const toggleCategoryVisibility = id => {
        setCategories(prev =>
            prev.map(c =>
                c.id === id
                    ? { ...c, isVisible: !c.isVisible, isActive: !c.isVisible }
                    : c,
            ),
        );
        onDirtyChange?.(true);
    };

    const toggleItemVisibility = id => {
        setItems(prev =>
            prev.map(i =>
                i.id === id
                    ? { ...i, isVisible: !i.isVisible, isActive: !i.isVisible }
                    : i,
            ),
        );
        onDirtyChange?.(true);
    };

    // ─── rename ───
    const updateCategoryName = (id, value) => {
        setCategories(prev =>
            prev.map(c =>
                c.id === id
                    ? {
                          ...c,
                          nameOverride: value,
                          categoryName: value || c.systemName,
                      }
                    : c,
            ),
        );
        onDirtyChange?.(true);
    };

    const updateItemName = (id, value) => {
        setItems(prev =>
            prev.map(i =>
                i.id === id
                    ? {
                          ...i,
                          nameOverride: value,
                          detailName: value || i.systemName,
                      }
                    : i,
            ),
        );
        onDirtyChange?.(true);
    };

    const revertCategoryName = id => updateCategoryName(id, '');
    const revertItemName = id => updateItemName(id, '');

    // ─── drag and drop (mirrors PlanTypeCategoriesPage / ItemPage) ───
    const handleDragStart = e => {
        const index = e.target.dataset.index;
        setDraggedIndex(index);
        e.dataTransfer.setData('text/plain', index);
        e.target.classList.add('dragging');
    };

    const handleDragOver = e => {
        e.preventDefault();
    };

    const handleDragEnd = e => {
        e.target.classList.remove('dragging');
        setDraggedIndex(null);
        setHoveredIndex(null);
    };

    const reorder = (arr, fromIdx, toIdx) => {
        const next = [...arr];
        const [moved] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, moved);
        return next.map((row, idx) => ({ ...row, sortOrder: idx + 1 }));
    };

    const handleDrop = e => {
        e.preventDefault();
        const targetIndex = hoveredIndex;
        let newIndex;
        if (draggedIndex < targetIndex) newIndex = targetIndex - 1;
        else newIndex = targetIndex;
        if (newIndex < 0) newIndex = 0;
        if (draggedIndex !== null && draggedIndex !== newIndex) {
            if (view === VIEWS.CATEGORIES) {
                setCategories(prev =>
                    reorder(prev, Number(draggedIndex), newIndex),
                );
            } else {
                setItems(prev => reorder(prev, Number(draggedIndex), newIndex));
            }
            onDirtyChange?.(true);
        }
        setDraggedIndex(null);
        setHoveredIndex(null);
    };

    const handleDropTop = e => {
        e.preventDefault();
        if (draggedIndex !== null) {
            if (view === VIEWS.CATEGORIES) {
                setCategories(prev => {
                    const next = [...prev];
                    const [moved] = next.splice(Number(draggedIndex), 1);
                    next.unshift(moved);
                    return next.map((r, i) => ({ ...r, sortOrder: i + 1 }));
                });
            } else {
                setItems(prev => {
                    const next = [...prev];
                    const [moved] = next.splice(Number(draggedIndex), 1);
                    next.unshift(moved);
                    return next.map((r, i) => ({ ...r, sortOrder: i + 1 }));
                });
            }
            onDirtyChange?.(true);
        }
        setDraggedIndex(null);
        setHoveredIndex(null);
    };

    // ─── save ───
    const handleSave = async () => {
        try {
            setIsSaving(true);
            if (view === VIEWS.CATEGORIES) {
                const payload = categories.map(c => ({
                    id: c.id,
                    clientPlanTypeId: c.clientPlanTypeId,
                    systemPlanDetailCategoryId: c.systemPlanDetailCategoryId,
                    sortOrder: c.sortOrder,
                    isActive: c.isVisible,
                    name: c.nameOverride || null,
                    planYearId,
                }));
                await updatePlanTypeDetailCategories(planTypeId, payload);
                Toast.success(getText('msg_57d6938b051f'));
            } else {
                const payload = items.map(i => ({
                    id: i.id,
                    planTypeDetailCategoryId: i.planTypeDetailCategoryId,
                    systemPlanDetailId: i.systemPlanDetailId,
                    sortOrder: i.sortOrder,
                    isActive: i.isVisible,
                    name: i.nameOverride || null,
                }));
                await updatePlanTypeDetail(activeCategory.id, payload);
                Toast.success(getText('msg_0469513434fc'));
            }
            onDirtyChange?.(false);
        } catch (error) {
            console.error('Save failed', error);
            Toast.error(
                error?.response?.data?.detail ||
                    'Save failed. Please try again.',
            );
        } finally {
            setIsSaving(false);
        }
    };

    // ─── row renderer — matches PlanTypeCategoriesPage markup verbatim ───
    const renderRows = (rows, kind /* 'category' | 'item' */) => {
        const onVisibility =
            kind === 'category'
                ? toggleCategoryVisibility
                : toggleItemVisibility;
        const onName =
            kind === 'category' ? updateCategoryName : updateItemName;
        const onRevert =
            kind === 'category' ? revertCategoryName : revertItemName;
        const showViewMore = kind === 'category';
        const labelKey = kind === 'category' ? 'categoryName' : 'detailName';
        // PlanTypeCategoriesPage used me-2 for grip; ItemPage used me-3. Keep that distinction.
        const gripMargin = kind === 'category' ? 'me-2' : 'me-3';

        const nodes = [];

        nodes.push(
            <div
                key="top-drop-zone"
                className={`mb-3 d-flex align-items-center justify-content-center rounded ${
                    hoveredIndex === -1
                        ? 'bg-info bg-opacity-10 border border-2 border-primary border-opacity-50 p-4'
                        : 'p-1'
                }`}
                onDragOver={handleDragOver}
                onDrop={handleDropTop}
                onDragEnter={() => setHoveredIndex(-1)}
                onDragLeave={() => setHoveredIndex(null)}
            >
                {hoveredIndex === -1 && (
                    <span className="text-muted">
                        <i className="bi bi-arrow-up me-2" />
                        {getText('msg_9dfd765eec6b')}
                    </span>
                )}
            </div>,
        );

        rows.forEach((row, index) => {
            const displayName = row[labelKey];
            const hidden = !row.isVisible;

            nodes.push(
                <div key={row.id} className="mb-1">
                    <div className="row align-items-center">
                        <div className="col-9">
                            <div
                                className={`card shadow-sm rounded-3 ${
                                    hoveredIndex === index
                                        ? 'border-primary border-2'
                                        : ''
                                } ${hidden ? 'bg-light text-muted' : ''}`}
                                draggable
                                data-index={index}
                                onDragStart={handleDragStart}
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                onDragEnd={handleDragEnd}
                                onDragEnter={() => setHoveredIndex(index)}
                                onDragLeave={() => setHoveredIndex(null)}
                            >
                                <div className="card-body pe-0 py-2">
                                    <div className="d-flex align-items-center">
                                        <div className={gripMargin}>
                                            <i className="bi bi-grip-vertical text-muted fs-5" />
                                        </div>
                                        <div className="d-flex flex-grow-1 align-items-center pe-2">
                                            {editNames ? (
                                                <>
                                                    <div
                                                        className={
                                                            styles.nameInputWrap
                                                        }
                                                    >
                                                        <input
                                                            type="text"
                                                            className={`form-control form-control-sm ${styles.nameInputField}`}
                                                            value={
                                                                row.nameOverride ||
                                                                row.systemName ||
                                                                ''
                                                            }
                                                            onChange={e =>
                                                                onName(
                                                                    row.id,
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            onClick={e =>
                                                                e.stopPropagation()
                                                            }
                                                        />
                                                        <button
                                                            type="button"
                                                            className={
                                                                styles.nameRevertBtn
                                                            }
                                                            title={getText('msg_d4ea57a9428e')}
                                                            onClick={() =>
                                                                onRevert(row.id)
                                                            }
                                                        >
                                                            <i className="bi bi-arrow-counterclockwise" />
                                                        </button>
                                                    </div>
                                                    {showViewMore && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-link text-decoration-none align-self-center ms-auto"
                                                            onClick={() =>
                                                                enterItems(row)
                                                            }
                                                        >
                                                            {getText('msg_40e91027f1df')}
                                                        </button>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    <h5 className="card-title mb-0 align-self-center">
                                                        {displayName}
                                                        {hidden && (
                                                            <i
                                                                className="bi bi-eye-slash ms-2"
                                                                aria-hidden="true"
                                                            />
                                                        )}
                                                    </h5>
                                                    {showViewMore && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-link text-decoration-none align-self-center ms-auto"
                                                            onClick={() =>
                                                                enterItems(row)
                                                            }
                                                        >
                                                            {getText('msg_40e91027f1df')}
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-3">
                            <div className="d-flex justify-content-center">
                                <div className="form-switch form-switch-locked">
                                    <input
                                        className="form-check-input custom-switch-input"
                                        type="checkbox"
                                        checked={!!row.isVisible}
                                        onChange={() => onVisibility(row.id)}
                                        onClick={e => e.stopPropagation()}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>,
            );

            nodes.push(
                <div
                    key={`drop-zone-${index}`}
                    className={`mb-2 d-flex align-items-center justify-content-center rounded ${
                        hoveredIndex === index + 1
                            ? 'bg-info bg-opacity-10 border border-2 border-primary border-opacity-50 p-3'
                            : 'p-1'
                    }`}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onDragEnter={() => setHoveredIndex(index + 1)}
                    onDragLeave={() => setHoveredIndex(null)}
                >
                    {hoveredIndex === index + 1 && (
                        <span className="text-muted small">
                            <i className="bi bi-arrow-down me-2" />
                            {getText('msg_7c67e6fb70f3')}
                        </span>
                    )}
                </div>,
            );
        });

        return nodes;
    };

    const rows = view === VIEWS.CATEGORIES ? categories : items;
    const isEmpty = rows.length === 0;
    const kind = view === VIEWS.CATEGORIES ? 'category' : 'item';

    return (
        <div>
            <div className={styles.descriptionBlock}>
                <h6>{getText('msg_ef23711a6800')}</h6>
                <p>
                    {getText('msg_aa00aaa8e08d')}
                </p>
            </div>

            {isLoading ? (
                <div className={styles.loadingState}>
                    <Spinner width={36} className={styles.loadingSpinner} />
                </div>
            ) : isEmpty ? (
                <NotFoundBox
                    backgroundColor="#f0f4ff"
                    title={
                        view === VIEWS.CATEGORIES ? 'No categories' : 'No items'
                    }
                    description={
                        view === VIEWS.CATEGORIES
                            ? 'No categories are configured for this plan type.'
                            : 'No items are configured for this category.'
                    }
                />
            ) : (
                /* matches PlanTypeCategoriesPage layout exactly */
                <div className="container-fluid px-4">
                    <div className="col-12 overflow-y-scroll overflow-x-hidden">
                        <div className="row align-items-end mb-2">
                            <div className="col-9">
                                {view === VIEWS.CATEGORIES ? (
                                    <button
                                        type="button"
                                        className={styles.editToggleLink}
                                        onClick={() => setEditNames(v => !v)}
                                    >
                                        {editNames
                                            ? 'Edit Properties'
                                            : 'Edit Category Names'}
                                    </button>
                                ) : (
                                    <span className={styles.categoryBreadcrumb}>
                                        <strong>{getText('msg_568993e7b8f8')} </strong>
                                        {activeCategory?.categoryName}
                                        <button
                                            type="button"
                                            className={`${styles.editToggleLink} ${styles.inlineEditToggle}`}
                                            onClick={() =>
                                                setEditNames(v => !v)
                                            }
                                        >
                                            {editNames
                                                ? 'Edit Properties'
                                                : 'Edit Item Names'}
                                        </button>
                                    </span>
                                )}
                            </div>
                            <div className="col-3">
                                <div className="d-flex justify-content-center">
                                    <h6 className={styles.visibleLabel}>
                                        {getText('msg_1fe59390acc1')}
                                    </h6>
                                </div>
                            </div>
                        </div>

                        <div className={styles.rowListScrollable}>
                            {renderRows(rows, kind)}
                        </div>
                    </div>
                </div>
            )}

            <div className="d-flex justify-content-between align-items-center mt-3 px-4">
                <p className="mb-0">
                    <span className="text-danger">*</span> {getText('msg_7c118f042eaa')}
                </p>

                <div className="d-flex align-items-center gap-3">
                    {view === VIEWS.ITEMS && (
                        <button
                            type="button"
                            className={styles.btnBack}
                            onClick={backToCategories}
                        >
                            <i className="bi bi-chevron-left me-1" /> {getText('msg_a3c686e711e4')}
                        </button>
                    )}

                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {isSaving ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};

PlanTypeCategoriesItemsTab.propTypes = {
    planTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    planYearId: PropTypes.number,
    onDirtyChange: PropTypes.func,
};

export default PlanTypeCategoriesItemsTab;
